import { useFormikContext } from 'formik';
import type { FC } from 'react';
import React, { useState } from 'react';
import { COLUMN_LEVEL } from '../../constants/column-types';
import { DEF_PG_SIZE_SUB_MIN } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import FloorColumns from '../../models/ColumnDefinitions/FloorColumn';
import { Floor } from '../../models/Floor';
import { ListQuery } from '../../models/ListQuery';
import FloorSearch from '../../models/search/FloorSearch';
import { getFloors } from '../../services/floorService';
import { appendFilter } from '../../utilities/filters';
import TableTab from '../tabs/table-tab';
import { WizardStepLayout } from '../wizard/wizard-summary-layout';
import { hideLookups, removeColumns } from '@/models/utilities/columnHelper';
import { TableColumn } from '@/models/table/TableColumn';
import { getLicenseLimit, LICENSE_ID_MAX_FLOORS_PER_LIFT } from '@/utilities/licenseLimits';

interface FloorStepProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onBack: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const FloorStep: FC<FloorStepProps> = ({ onNext, onBack, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { values, setFieldValue } = useFormikContext<any>();
    const [selectedFloorIds, setSelectedFloorIds] = useState<string[]>(values.floorIds || []);
    const [selectedRows, setSelectedRows] = useState<Floor[]>(values.floors || []);
    const maxFloorsPerLift = getLicenseLimit(LICENSE_ID_MAX_FLOORS_PER_LIFT);

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setStepDescriptions(prev => ({ ...prev, step3: translate.byKeyFormatted(TranslationKeys.selectedWithCount, { count: selectedFloorIds.length }) }));
        setFieldValue('floorIds', selectedFloorIds.sort((a, b) => parseInt(a) - parseInt(b)));
        setFieldValue('floors', selectedRows.sort((a, b) => parseInt(a.level) - parseInt(b.level)));
        onNext(e);
    }

    const onLocalBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setFieldValue('floorIds', []);
        setFieldValue('floors', []);
        setStepDescriptions(prev => ({ ...prev, step3: translate.byKey(TranslationKeys.pending) }));
        onBack(e);
    }

    const getFloorsListParameters = (listParameters?: ListQuery): ListQuery => {
        const buildingFilter = appendFilter(values.buildingId, 'Building id', 'building.id', listParameters?.filters ?? []);
        const floorRange = appendFilter(values.startingFloor, 'Floor Level', 'floorLevel', buildingFilter, '>='); 
        const floorRangeEnd = appendFilter(values.lastFloor, 'Floor Level', 'floorLevel', floorRange, '<=');
        return { ...listParameters, filters: floorRangeEnd, sortBy: { columnId: parseInt(COLUMN_LEVEL), columnName: 'floorLevel', direction: 'asc' } };
    }

    const getFloorColumns = (): TableColumn[] => {
        const columns = removeColumns(FloorColumns, ['actionName', 'deviceName'])
        return hideLookups(columns, ['building', 'site']);
    };

    return <WizardStepLayout onBack={onLocalBack} onNext={onLocalNext} nextDisabled={!(selectedFloorIds.length > 0 && selectedFloorIds.length <= maxFloorsPerLift)}>
        <div className='flex flex-col space-y-1'>
        <span className={`text-portal-blue-primary-shade2 text-xl font-semibold`} >
            {translate.byKey(TranslationKeys.selectFloors)}
        </span>
        <span className="text-sm">
            {translate.byKey(TranslationKeys.chooseLiftFloors)}
        </span>
        <div className='flex w-full' style={{ maxHeight: '350px' }}>
            <TableContextProvider
                dataMethod={(listParameters?: ListQuery) => getFloors({ ...getFloorsListParameters(listParameters)})}
                tableDescriptor={getFloorColumns()}
                searchDescriptor={FloorSearch}
                useTable
                usePagination
                useFilter
                useSearch
                useSort
                maxPageSize={DEF_PG_SIZE_SUB_MIN}
                canExportReport={false}>
                <TableTab
                    title={''}
                    selectRows={true}
                    cyName="floor"
                    selectedIds={selectedFloorIds}
                    setSelectedIds={setSelectedFloorIds}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows} />
            </TableContextProvider>
        </div>
        </div>
    </WizardStepLayout>
}

export default FloorStep;